import React, { useCallback } from 'react';
import { Card, Group, ActionIcon, Box, Collapse } from '@mantine/core';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useDashboardPreferences } from './useDashboardPreferences.tsx';

export interface DashboardCardProps {
  id: string;
  title: string;
  summary: React.ReactNode;
  details?: React.ReactNode;
  badges?: React.ReactNode;
  defaultExpanded?: boolean;
  className?: string;
}

export const DashboardCard: React.FC<DashboardCardProps> = ({
  id,
  title,
  summary,
  details,
  badges,
  defaultExpanded = false,
  className
}) => {
  const { expanded, toggleExpanded } = useDashboardPreferences();
  const isExpanded = expanded.includes(id) || defaultExpanded;

  const handleToggle = useCallback(() => {
    toggleExpanded(id);
    // Trigger masonry re-measure immediately & after collapse animation
    window.dispatchEvent(new Event('dashboard-masonry-measure'));
    setTimeout(() => window.dispatchEvent(new Event('dashboard-masonry-measure')), 240); // after Collapse transition
  }, [id, toggleExpanded]);

  return (
    <Card withBorder radius="md" p="sm" className={className} data-card-id={id}>
      <Group justify="space-between" gap="xs" mb="xs" wrap="nowrap">
        <Group gap={4} style={{ cursor: details ? 'pointer' : 'default' }} onClick={details ? handleToggle : undefined}>
          {details && (
            <ActionIcon variant="subtle" size="sm" aria-label={isExpanded ? 'Collapse' : 'Expand'}>
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </ActionIcon>
          )}
          <Box component="h3" id={`card-title-${id}`} style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>
            {title}
          </Box>
        </Group>
        {badges && <Group gap={4}>{badges}</Group>}
      </Group>
      <Box>{summary}</Box>
      {details && (
        <Collapse in={isExpanded} transitionDuration={160}>
          <Box mt="sm">{details}</Box>
        </Collapse>
      )}
    </Card>
  );
};